import { useTranslation } from "react-i18next";

import type { AccountTypeId, InstrumentGroupId } from "./instruments";
import type { KnownLadderTierKey, LadderTierKey } from "./types";

/**
 * Every string the widget renders, keyed with an English default.
 *
 * No section component may hold a literal — they all read from here, so the
 * whole slice is translatable from one place and `packages/ui`'s test setup
 * (which initialises i18next with `resources: {}`) asserts against these
 * defaults.
 */
export const useRebateTierTranslatedContent = () => {
  const { t } = useTranslation();

  /**
   * Display names for ladder tiers. The transport supplies a name for the
   * partner's *current* tier only, so the ladder table needs its own.
   */
  const ladderTierNames: Record<KnownLadderTierKey, string> = {
    bronze: t("rebateWidget_tier_bronze", "Bronze"),
    silver: t("rebateWidget_tier_silver", "Silver"),
    gold: t("rebateWidget_tier_gold", "Gold"),
    platinum: t("rebateWidget_tier_platinum", "Platinum"),
    brilliant: t("rebateWidget_tier_brilliant", "Brilliant"),
    elite: t("rebateWidget_tier_elite", "Elite"),
  };

  /** Instrument family names shown in the Rebate details sheet. */
  const instrumentGroupNames: Record<InstrumentGroupId, string> = {
    fx: t("rebateWidget_instrumentGroup_fx", "FX"),
    commodity: t("rebateWidget_instrumentGroup_commodity", "Commodity"),
    cfd: t("rebateWidget_instrumentGroup_cfd", "CFD"),
    equity: t("rebateWidget_instrumentGroup_equity", "Equity"),
    crypto: t("rebateWidget_instrumentGroup_crypto", "Crypto"),
    treasuries: t("rebateWidget_instrumentGroup_treasuries", "Treasuries"),
    spreadBetting: t(
      "rebateWidget_instrumentGroup_spreadBetting",
      "Spread betting",
    ),
  };

  /** Account types the rebate rate varies by. */
  const accountTypeNames: Record<AccountTypeId, string> = {
    standard: t("rebateWidget_accountType_standard", "Standard"),
    razor: t("rebateWidget_accountType_razor", "Razor"),
    islamic: t("rebateWidget_accountType_islamic", "Swap-free"),
  };

  return {
    ladderTierName: (key: LadderTierKey) =>
      Object.hasOwn(ladderTierNames, key)
        ? ladderTierNames[key as KnownLadderTierKey]
        : `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    instrumentGroupName: (id: InstrumentGroupId) => instrumentGroupNames[id],
    /** Family label for an API asset class, falling back to its raw name. */
    assetGroupName: (groupId: InstrumentGroupId | null, asset: string) =>
      groupId ? instrumentGroupNames[groupId] : asset,
    accountTypeName: (id: AccountTypeId) => accountTypeNames[id],

    // Header
    title: t("rebateWidget_title", "Rebate tier"),
    currentTier: t("rebateWidget_currentTier", "Current tier"),
    learnMore: t("rebateWidget_learnMore", "Learn more"),
    rebate: t("rebateWidget_rebate", "Rebate"),
    upTo: t("rebateWidget_upTo", "Up to"),
    currencyPerLot: (currency: string) =>
      t("rebateWidget_currencyPerLot", {
        defaultValue: "{{currency}} per lot",
        currency,
      }),
    viewDetails: t("rebateWidget_viewDetails", "View details"),

    // Criteria progress
    progressToUpgrade: t(
      "rebateWidget_progressToUpgrade",
      "Progress to upgrade tier",
    ),
    progressToMaintain: t(
      "rebateWidget_progressToMaintain",
      "Progress to maintain current tier",
    ),
    yourPerformance: t("rebateWidget_yourPerformance", "Your performance"),
    daysRemaining: (count: number) =>
      count === 1
        ? t("web_rebate_widget_countdown_label_one_day", "1 day")
        : t("rebateWidget_daysRemaining", {
            defaultValue: "{{count}} days",
            count,
          }),
    tradingVolume: t("rebateWidget_tradingVolume", "Trading volume"),
    activeClients: t("rebateWidget_activeClients", "Active clients"),
    newClients: t("rebateWidget_newClients", "New clients"),
    millionUsd: t("rebateWidget_millionUsd", "million USD"),
    million: t("rebateWidget_million", "million"),
    tradingVolumeTooltip: t(
      "rebateWidget_tradingVolumeTooltip",
      "Total closed trade volume from your direct clients over the 3-month qualifying window.",
    ),
    activeClientsTooltip: t(
      "rebateWidget_activeClientsTooltip",
      "Active clients are defined as unique clients who have traded within 3 calendar months.",
    ),
    newClientsTooltip: t(
      "rebateWidget_newClientsTooltip",
      "New clients are clients who joined and first traded within 3 calendar months.",
    ),
    criterionMet: t("rebateWidget_criterionMet", "Criterion met"),

    // In-widget message bars
    eventNotification: (tier: string) =>
      t("rebateWidget_eventNotification", {
        defaultValue:
          "New user perk: you've been boosted to {{tier}} tier! Complete your profile and meet the criteria before your boost expires to keep it.",
        tier,
      }),
    completeProfile: t(
      "rebateWidget_completeProfile",
      "Complete your profile to unlock higher tiers and more rebates",
    ),
    fraudulentBanner: t(
      "rebateWidget_fraudulentBanner",
      "Your tier progression is on hold while we review your account. Contact support if you need help.",
    ),
    graceNotice: t(
      "rebateWidget_graceNotice",
      "You've been granted a grace period to maintain your tier. Meet the criteria before the end to maintain your current tier.",
    ),
    // The on-card grace bar (canvas node 36681-62133) carries its own copy
    // with the grace end date — distinct from `graceNotice`, which is the
    // sheet/popover notice shown inside the criteria-explainer and
    // qualifying-period overlays.
    graceActiveNotice: (date: string) =>
      t("rebateWidget_graceActiveNotice", {
        defaultValue:
          "Grace period active until {{date}}. Meet the criteria before the end, or your tier will be downgraded.",
        date,
      }),
    frozenNotice: t(
      "rebateWidget_frozenNotice",
      "Your tier is protected for a limited time and won't change based on performance",
    ),
    protectionEnds: t(
      "web_rebate_widget_protection_ends",
      "Tier protection ends",
    ),
    protectionBody: t(
      "web_rebate_widget_protection_body",
      "Your tier is protected for a limited time and won't change based on performance.",
    ),
    protectionAfter: t(
      "web_rebate_widget_protection_after",
      "When protection ends, it's adjusted according to your performance over a rolling 3 month window.",
    ),
    protectionDays: (days: number | null) => {
      if (days === null)
        return t("web_rebate_widget_protection_days_unknown", "- days");
      return days <= 1
        ? t("web_rebate_widget_protection_day", {
            defaultValue: "{{days}} day",
            days,
          })
        : t("web_rebate_widget_protection_days", {
            defaultValue: "{{days}} days",
            days,
          });
    },
    qualificationCountdownNotice: (tier: string, days: number) =>
      days === 1
        ? t("web_rebate_widget_notification_body_at_risk_one_day", {
            defaultValue:
              "You’re at risk of losing {{tier}}. Meet the criteria in 1 day to keep your current tier.",
            tier,
          })
        : t("rebateWidget_qualificationCountdownNotice", {
            defaultValue:
              "You’re at risk of losing {{tier}}. Meet the criteria in {{days}} days to keep your current tier.",
            tier,
            days,
          }),

    // Criteria explainer sheet
    criteriaExplainerTitle: t(
      "rebateWidget_criteriaExplainer_title",
      "How criteria are measured",
    ),
    criteriaExplainerBody: t(
      "rebateWidget_criteriaExplainer_body",
      "Trading volume is defined by close trade executed by your direct clients. We assess the criteria to qualify for partner tiers using a 3-month window.",
    ),
    currentProgress: t("rebateWidget_currentProgress", "Current progress"),
    dataLastUpdated: (datetime: string) =>
      t("rebateWidget_dataLastUpdated", {
        defaultValue: "Data last updated: {{datetime}}",
        datetime,
      }),
    threeMonthTarget: t(
      "rebateWidget_threeMonthTarget",
      "3-month target (USD)",
    ),
    threeMonthClientTarget: t(
      "rebateWidget_threeMonthClientTarget",
      "3-month target",
    ),
    activeClientsTargetSuffix: (target: string) =>
      t("web_rebate_widget_explainer_label_target_unique_clients", {
        defaultValue: "/{{target}} unique clients",
        target,
      }),
    newClientsTargetSuffix: (target: string) =>
      t("web_rebate_widget_explainer_label_target_clients", {
        defaultValue: "/{{target}} clients",
        target,
      }),
    currentMonth: t("rebateWidget_currentMonth", "Current month"),

    // Qualifying period / cycle sheet
    cycleTitle: t("rebateWidget_cycle_title", "Qualifying period"),
    cycleEnds: t("rebateWidget_cycle_ends", "Cycle ends"),
    qualifyingWindow: t(
      "rebateWidget_cycle_qualifyingWindow",
      "Qualifying window",
    ),
    // The canvas ends this sentence with an inline link, so the copy is split
    // at that point. The link label carries the full stop so no punctuation
    // literal has to live in JSX.
    cycleBody: t(
      "rebateWidget_cycle_body",
      "We review your performance using a 3-month cycle. To move to the next tier, your performance within 3 months before the cycle end date must meet our",
    ),
    criteriaLink: t("rebateWidget_cycle_criteriaLink", "criteria."),

    // Tier transition message bars. No Figma frame exists for freeze, so
    // that copy is a structural placeholder alongside the placeholder
    // visuals in `WidgetNotification`. Upgrade's copy matches the canvas at
    // node 36681-61651; downgrade's matches node 36681-60299; unfreeze's
    // matches node 36684-58820.
    tierUpgradeNotification: (tier: string) =>
      t("rebateWidget_tierUpgradeNotification", {
        defaultValue:
          "You’ve unlocked {{tier}}! Enjoy greater earnings with higher rebates",
        tier,
      }),
    tierDowngradeNotification: (tier: string) =>
      t("rebateWidget_tierDowngradeNotification", {
        defaultValue:
          "Based on your recent activity, you've moved to {{tier}} and your rebate has been adjusted to match",
        tier,
      }),
    tierFreezeNotification: t(
      "rebateWidget_tierFreezeNotification",
      "Your tier is protected for a limited time and won’t change based on performance",
    ),
    tierUnfreezeNotification: (tier: string) =>
      t("rebateWidget_tierUnfreezeNotification", {
        defaultValue:
          "Your tier protection has ended. Based on your performance, your new tier is {{tier}}.",
        tier,
      }),

    // Volume converter sheet
    volumeConverterTitle: t(
      "rebateWidget_volumeConverter_title",
      "View volume as",
    ),
    volumeConverterNotice: (datetime: string) =>
      t("rebateWidget_volumeConverter_notice", {
        defaultValue:
          "Lot sizes are for reference only. Last updated: {{datetime}}",
        datetime,
      }),
    volumeConverterUsd: t("rebateWidget_volumeConverter_usd", "USD"),
    volumeConverterUsdDescription: t(
      "rebateWidget_volumeConverter_usdDescription",
      "View volume as USD value",
    ),
    volumeConverterDefaultBadge: t(
      "rebateWidget_volumeConverter_defaultBadge",
      "Default",
    ),
    volumeConverterLotEquivalent: (value: string) =>
      t("rebateWidget_volumeConverter_lotEquivalent", {
        defaultValue: "1 lot ≈ US${{value}}",
        value,
      }),
    volumeConverterLoadError: t(
      "web_rebate_widget_converter_error_body_load_failed",
      "Unable to load data, refresh to try again",
    ),
    saveAsDefault: t("rebateWidget_saveAsDefault", "Save as default"),
    saveDefaultErrorTitle: t(
      "web_rebate_widget_converter_toast_title_save_failed",
      "Unable to save the lot view as default",
    ),
    saveDefaultErrorBody: t(
      "web_rebate_widget_converter_toast_body_save_failed",
      "Something is wrong with saving the lot view as default, try again later",
    ),
    apply: t("rebateWidget_apply", "Apply"),
    /** Unit suffix on the value row once a symbol is applied ("lots XAUUSD"). */
    lotsOfSymbol: (symbol: string) =>
      t("rebateWidget_lotsOfSymbol", {
        defaultValue: "lots {{symbol}}",
        symbol,
      }),
    explainCriterion: (criterion: string) =>
      t("rebateWidget_explainCriterion", {
        defaultValue: "How {{criterion}} is measured",
        criterion,
      }),

    // Partner tiers modal
    partnerTiersTitle: t("rebateWidget_partnerTiers_title", "Partner tiers"),
    partnerTiersOverviewTab: t(
      "rebateWidget_partnerTiers_overviewTab",
      "Overview",
    ),
    partnerTiersCriteriaTab: t(
      "rebateWidget_partnerTiers_criteriaTab",
      "Tiers and criteria",
    ),

    // Partner tiers — Overview tab
    howTiersWorkTitle: t(
      "rebateWidget_partnerTiers_howTiersWorkTitle",
      "How do Partner tiers work?",
    ),
    tierUpgradeTitle: t(
      "rebateWidget_partnerTiers_tierUpgradeTitle",
      "Tier upgrade",
    ),
    tierUpgradeBody: t(
      "rebateWidget_partnerTiers_tierUpgradeBody",
      "Meet the criteria to automatically upgrade and unlock higher rebates with more commission benefits.",
    ),
    tierDowngradeTitle: t(
      "rebateWidget_partnerTiers_tierDowngradeTitle",
      "Tier downgrade",
    ),
    tierDowngradeBody: t(
      "rebateWidget_partnerTiers_tierDowngradeBody",
      "If your performance drops below the requirements before the monthly evaluation, you'll move to the tier that matches your results.",
    ),
    howToQualifyTitle: t(
      "rebateWidget_partnerTiers_howToQualifyTitle",
      "How to qualify",
    ),
    howToQualifyBody: t(
      "rebateWidget_partnerTiers_howToQualifyBody",
      "Evaluation is based on criteria which is assessed over a rolling three-month period. To qualify for the tier, the sum of the metrics must meet the specified criteria during this timeframe.",
    ),
    exampleTitle: t("rebateWidget_partnerTiers_exampleTitle", "Example"),
    exampleBody: t(
      "rebateWidget_partnerTiers_exampleBody",
      "If the current month is March, your performance will be evaluated based on January, February, and March.",
    ),
    threeMonthTargetLabel: t(
      "rebateWidget_partnerTiers_threeMonthTargetLabel",
      "3-month target",
    ),
    monthTarget: (month: string) =>
      t("rebateWidget_partnerTiers_monthTarget", {
        defaultValue: "{{month}} target",
        month,
      }),
    millionsShort: (value: string) =>
      t("rebateWidget_millionsShort", { defaultValue: "{{value}}M", value }),

    // Partner tiers — Tiers and criteria tab
    criteriaTitle: t("rebateWidget_partnerTiers_criteriaTitle", "Criteria"),
    currentTierPill: t("rebateWidget_partnerTiers_currentTierPill", "Current"),
    atLeastVolume: (value: string) =>
      t("rebateWidget_partnerTiers_atLeastVolume", {
        defaultValue: "≥ {{value}} million USD",
        value,
      }),
    atLeastCount: (value: string) =>
      t("rebateWidget_partnerTiers_atLeastCount", {
        defaultValue: "≥ {{value}}",
        value,
      }),
    rebateStructureTitle: t(
      "rebateWidget_partnerTiers_rebateStructureTitle",
      "Rebate structure",
    ),
    rebateStructureBody: t(
      "rebateWidget_partnerTiers_rebateStructureBody",
      "Your rebate structure is dependent on your clients' account type and instrument being traded. The rebate is calculated based on minimum spread.",
    ),

    // Rebate details modal
    rebateDetailsTitle: t("rebateWidget_rebateDetails_title", "Rebate details"),
    rebateDetailsBody: t(
      "rebateWidget_rebateDetails_body",
      "Your rebate depends on your clients’ account type and the instrument traded. The rebate value listed below is calculated on a per-lot basis and may vary based on the currency exchange rate.",
    ),
    /** Shared by the Rebate details and Rebate calculator sheets. */
    fxRateDisclaimer: (date: string) =>
      t("rebateWidget_fxRateDisclaimer", {
        defaultValue:
          "Data provided is based on the currency exchange rate from {{date}}",
        date,
      }),
    searchBySymbol: t(
      "rebateWidget_rebateDetails_searchBySymbol",
      "Search by symbol",
    ),
    filterTier: t("rebateWidget_rebateDetails_filterTier", "Tier"),
    filterAccountType: t(
      "rebateWidget_rebateDetails_filterAccountType",
      "Account type",
    ),
    filterTierMenuTitle: t(
      "rebateWidget_rebateDetails_filterTierMenu",
      "Tiers",
    ),
    filterAccountTypeMenuTitle: t(
      "rebateWidget_rebateDetails_filterAccountTypeMenu",
      "Account types",
    ),
    tierOptionLabel: (tier: string) =>
      t("rebateWidget_rebateDetails_tierOptionLabel", {
        defaultValue: "{{tier}} tier",
        tier,
      }),
    /** The partner's own tier, marked out in the tier filter's list. */
    tierOptionCurrentLabel: (tier: string) =>
      t("rebateWidget_rebateDetails_tierOptionCurrentLabel", {
        defaultValue: "{{tier}} (current tier)",
        tier,
      }),
    columnInstrument: t(
      "rebateWidget_rebateDetails_columnInstrument",
      "Instrument",
    ),
    perLot: t("rebateWidget_rebateDetails_perLot", "Per lot"),
    upToRate: (value: string, currency: string) =>
      t("rebateWidget_rebateDetails_upToRate", {
        defaultValue: "Up to {{value}} {{currency}}",
        value,
        currency,
      }),
    exactRate: (value: string, currency: string) =>
      t("rebateWidget_rebateDetails_exactRate", {
        defaultValue: "{{value}} {{currency}}",
        value,
        currency,
      }),
    noInstrumentsFound: t(
      "rebateWidget_rebateDetails_noInstrumentsFound",
      "No instruments match your search",
    ),
    showInstruments: (group: string) =>
      t("rebateWidget_rebateDetails_showInstruments", {
        defaultValue: "Show {{group}} instruments",
        group,
      }),
    hideInstruments: (group: string) =>
      t("rebateWidget_rebateDetails_hideInstruments", {
        defaultValue: "Hide {{group}} instruments",
        group,
      }),
    openRebateCalculator: t(
      "rebateWidget_rebateDetails_openRebateCalculator",
      "Rebate calculator",
    ),
    /** Desktop only — the inline promo row replacing mobile's sticky CTA. */
    rebateCalculatorPromo: t(
      "rebateWidget_rebateDetails_rebateCalculatorPromo",
      "Not sure how much you’ll earn per trade?  Try our rebate calculator",
    ),
    calculateYourRebate: t(
      "rebateWidget_rebateDetails_calculateYourRebate",
      "Calculate your rebate",
    ),

    // Rebate calculator modal
    rebateCalculatorTitle: t(
      "rebateWidget_rebateCalculator_title",
      "Rebate calculator",
    ),
    selectAccountType: t(
      "rebateWidget_rebateCalculator_selectAccountType",
      "Select account type",
    ),
    tradingSymbol: t(
      "rebateWidget_rebateCalculator_tradingSymbol",
      "Trading symbol",
    ),
    selectTradingSymbol: t(
      "rebateWidget_rebateCalculator_selectTradingSymbol",
      "Select trading symbol",
    ),
    numberOfLots: t(
      "rebateWidget_rebateCalculator_numberOfLots",
      "Number of lots",
    ),
    estimatedTotalRebate: t(
      "rebateWidget_rebateCalculator_estimatedTotalRebate",
      "Estimated total rebate",
    ),
    emptyAmount: t("rebateWidget_rebateCalculator_emptyAmount", "––"),
    calculateRebate: t(
      "rebateWidget_rebateCalculator_calculateRebate",
      "Calculate rebate",
    ),

    // Widget error state
    errorStateMessage: t(
      "web_rebate_widget_error_body_load_failed",
      "Unable to load rebate tier, try again later",
    ),
    errorStateRefresh: t("web_rebate_widget_error_btn_refresh", "Refresh"),

    // Section load failures
    sectionErrorTitle: t(
      "rebateWidget_sectionError_title",
      "Something went wrong",
    ),
    sectionErrorBody: t(
      "rebateWidget_sectionError_body",
      "We couldn't load this information. Please try again.",
    ),
    sectionErrorRetry: t("rebateWidget_sectionError_retry", "Try again"),

    // Generic
    close: t("rebateWidget_close", "Close"),
  };
};

export type RebateTierTranslatedContent = ReturnType<
  typeof useRebateTierTranslatedContent
>;
